import { maxOperatorChartToolPoints } from "../constants";
import type {
  OperatorCategoricalData,
  OperatorCategoricalDataSlice,
  OperatorChartData,
  OperatorChartDataSliceSeries,
  OperatorChartDataSlice,
  OperatorChartDataSliceOptions,
  OperatorChartSeries,
  OperatorTimeSeriesData,
  OperatorTimeSeriesDataSlice,
} from "../types";

const getSeriesDataSlice = <T extends OperatorChartSeries>(
  series: T[],
  startIndex: number,
  pointsPerSeries: number
): OperatorChartDataSliceSeries<T>[] => {
  return series.map(({ data: seriesData, ...seriesMetadata }) => ({
    ...seriesMetadata,
    dataStartIndex: startIndex,
    totalPointCount: seriesData.length,
    data: seriesData.slice(startIndex, startIndex + pointsPerSeries),
  }));
};

const selectSeries = <T extends OperatorChartSeries>(series: T[], seriesNames?: string[] | null): T[] => {
  return seriesNames?.length ? series.filter(({ name }) => seriesNames.includes(name)) : series;
};

const getSeriesPage = <T extends OperatorChartSeries>(
  series: T[],
  seriesNames: string[] | null | undefined,
  maxPoints: number
) => {
  const selectedSeries = selectSeries(series, seriesNames);
  const returnedSeries = selectedSeries.slice(0, maxPoints);
  const pointsPerSeries = Math.max(1, Math.floor(maxPoints / Math.max(1, returnedSeries.length)));

  return { selectedSeries, returnedSeries, pointsPerSeries };
};

const getPage = (
  data: OperatorChartData,
  selectedSeries: OperatorChartSeries[],
  returnedSeries: OperatorChartSeries[],
  boundedStartIndex: number,
  boundedMaxPoints: number,
  pointsPerSeries: number
): OperatorTimeSeriesDataSlice["page"] => {
  const returnedPointCount = returnedSeries.reduce((total, { data: seriesData }) => {
    return total + seriesData.slice(boundedStartIndex, boundedStartIndex + pointsPerSeries).length;
  }, 0);

  return {
    startIndex: boundedStartIndex,
    maxPoints: boundedMaxPoints,
    returnedPointCount,
    hasMore:
      returnedSeries.some(({ data: seriesData }) => {
        return boundedStartIndex + Math.min(pointsPerSeries, seriesData.length) < seriesData.length;
      }) || returnedSeries.length < selectedSeries.length,
    availableSeriesNames: data.series.map(({ name }) => name),
    omittedSeriesNames: selectedSeries.slice(returnedSeries.length).map(({ name }) => name),
  };
};

/**
 * Returns a bounded, evenly distributed slice across the requested series so
 * a chart-data tool call cannot grow in direct proportion to the full chart.
 */
export function getOperatorTimeSeriesDataSlice(
  data: OperatorTimeSeriesData,
  options: OperatorChartDataSliceOptions
): OperatorTimeSeriesDataSlice;
export function getOperatorTimeSeriesDataSlice(
  data: OperatorCategoricalData,
  options: OperatorChartDataSliceOptions
): OperatorCategoricalDataSlice;
export function getOperatorTimeSeriesDataSlice(
  data: OperatorChartData,
  options: OperatorChartDataSliceOptions
): OperatorChartDataSlice;
export function getOperatorTimeSeriesDataSlice(
  data: OperatorChartData,
  { seriesNames, startIndex, maxPoints }: OperatorChartDataSliceOptions
): OperatorChartDataSlice {
  const boundedStartIndex = Math.max(0, Math.floor(startIndex ?? 0));
  const boundedMaxPoints = Math.min(
    maxOperatorChartToolPoints,
    Math.max(1, Math.floor(maxPoints ?? maxOperatorChartToolPoints))
  );
  const metadata = data.metadata !== undefined ? { metadata: data.metadata } : {};

  if (data.type === "categorical") {
    const { selectedSeries, returnedSeries, pointsPerSeries } = getSeriesPage<
      OperatorCategoricalData["series"][number]
    >(data.series, seriesNames, boundedMaxPoints);
    return {
      type: "categorical",
      xAxis: data.xAxis,
      series: getSeriesDataSlice<OperatorCategoricalData["series"][number]>(
        returnedSeries,
        boundedStartIndex,
        pointsPerSeries
      ),
      ...metadata,
      page: getPage(data, selectedSeries, returnedSeries, boundedStartIndex, boundedMaxPoints, pointsPerSeries),
    };
  }

  const { selectedSeries, returnedSeries, pointsPerSeries } = getSeriesPage<
    OperatorTimeSeriesData["series"][number]
  >(data.series, seriesNames, boundedMaxPoints);
  return {
    type: "time_series",
    xAxis: data.xAxis,
    series: getSeriesDataSlice<OperatorTimeSeriesData["series"][number]>(
      returnedSeries,
      boundedStartIndex,
      pointsPerSeries
    ),
    ...metadata,
    page: getPage(data, selectedSeries, returnedSeries, boundedStartIndex, boundedMaxPoints, pointsPerSeries),
  };
}
